import { z } from "zod";

import { npiNumberSchema } from "@/src/models/common-schema";
import type { Practitioner } from "@/src/models/practitioner";

export enum ReferralAuthorizationDetailsAction {
  CONTINUE = "continue",
  REQUEST = "request",
}

export const referralAuthorizationDetailsSchema = z
  .object({
    authorizationNumber: z.string().trim().min(1),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    numberOfServices: z.number().int().positive(),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "End date must be on or after the start date",
    path: ["endDate"],
  });

export const referralAuthorizationDraftSchema = z.object({
  authorizationNumber: z.string().trim().min(1).nullable(),
  startDate: z.iso.date().nullable(),
  endDate: z.iso.date().nullable(),
  numberOfServices: z.number().int().positive().nullable(),
});

/** The practitioner row when the pick is one Valerie already has; picks from the NPI registry have none. */
export const referralAuthorizationProviderSchema = z.object({
  practitionerId: z.uuid().nullable().optional(),
  npiNumber: npiNumberSchema,
  givenName: z.string().nullable(),
  familyName: z.string().nullable(),
});

/** A yes/no question a customer adds to the form; the answer lists the keys ops checked. */
export const referralAuthorizationCheckboxSchema = z.object({ key: z.string(), label: z.string() });

export const referralAuthorizationCheckboxStateSchema = referralAuthorizationCheckboxSchema.extend({
  checked: z.boolean(),
});

export type ReferralAuthorizationDetails = z.infer<typeof referralAuthorizationDetailsSchema>;
export type ReferralAuthorizationDraft = z.infer<typeof referralAuthorizationDraftSchema>;
export type ReferralAuthorizationProvider = z.infer<typeof referralAuthorizationProviderSchema>;
export type ReferralAuthorizationCheckbox = z.infer<typeof referralAuthorizationCheckboxSchema>;

/** The provider as the form sends it; null when there is none or its NPI isn't valid. */
export function toReferralAuthorizationProvider(
  practitioner: Pick<Practitioner, "id" | "npiNumber" | "givenName" | "familyName"> | null | undefined
): ReferralAuthorizationProvider | null {
  if (!practitioner) return null;

  return (
    referralAuthorizationProviderSchema.safeParse({
      practitionerId: practitioner.id || null,
      npiNumber: practitioner.npiNumber,
      givenName: practitioner.givenName ?? null,
      familyName: practitioner.familyName ?? null,
    }).data ?? null
  );
}

// Answers saved before these fields existed have no such keys.
const authorizingProviderSchema = referralAuthorizationProviderSchema.nullable().optional();
const checkedSchema = z.array(z.string()).optional();

export const referralAuthorizationDetailsAnswerSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal(ReferralAuthorizationDetailsAction.CONTINUE),
    authorization: referralAuthorizationDetailsSchema,
    authorizingProvider: authorizingProviderSchema,
    checked: checkedSchema,
  }),
  z.object({
    action: z.literal(ReferralAuthorizationDetailsAction.REQUEST),
    authorization: referralAuthorizationDraftSchema,
    authorizingProvider: authorizingProviderSchema,
    checked: checkedSchema,
  }),
]);

export type ReferralAuthorizationDetailsAnswer = z.infer<typeof referralAuthorizationDetailsAnswerSchema>;
